import type { SensorData } from 'Simulations/SensorPoint';
import type { StageContext } from './Context';
import { Notification, NotificationType } from '@visualizer/types';
import Grid from 'Geodata/grid';

/**
 * Stage types available in the pipeline
 */
export enum StageType {
    FILTER = 'filter',
    TRANSFORM = 'transform',
    PREPROCESS = 'preprocess',
}

export enum StageExecutionMode {
    BY_FLOOR = 'byFloor',
    BY_COLUMN = 'byColumn',
    ALL = 'all',
}

export type StageParameters = {
    executionMode?: StageExecutionMode;
};

/**
 * Abstract base class for pipeline stages
 * Concrete stages extend this class and implement the process method
 * Each instance maintains its own state between executions
 */
export abstract class PipelineStage<T extends StageParameters> {
    // We can't enforce static metadata, but we document it

    readonly id: string;

    private params: T;

    private previousFloor: SensorData[] = [];

    private notifications: Notification[] = [];

    constructor(params: T, _type: StageType) {
        this.id = crypto.randomUUID(); // or custom id generation
        this.params = params;
    }

    /**
     * A function that validate the execution of this stage
     */
    protected abstract validate(input: StageContext, output: StageContext): void;

    /**
     * Main execution method from the pipeline. This is communicating from a stage to another. You should never override this method
     */
    public execute(input: StageContext, params?: T): StageContext {
        if (params) this.setParams(params);
        const output = this.runExecutionMode(input);
        this.validate(input, output);
        return output;
    }

    private runExecutionMode(input: StageContext): StageContext {
        switch (this.getParams().executionMode) {
            case StageExecutionMode.BY_FLOOR:
                return this.processByFloor(input);
            case StageExecutionMode.BY_COLUMN:
                return this.processByColumn(input);
            case StageExecutionMode.ALL:
                return this.processAll(input);
            default:
                return this.processAll(input);
        }
    }
    /**
     * Process all sensor data
     */
    protected processAll(context: StageContext): StageContext {
        const outputSensors = context.sensors.map((sensorData) => this.process(sensorData, context)).filter((sd) => sd !== null);
        return { ...context, sensors: outputSensors };
    }

    protected processByFloor(context: StageContext): StageContext {
        const sensorsByFloor = context.sensors.reduce<Map<number, SensorData[]>>((map, s) => {
            const floor = s.getSensorPoint().getFloor();
            if (!map.has(floor)) map.set(floor, []);
            map.get(floor)!.push(s);
            return map;
        }, new Map());

        let localContext: StageContext = context;

        for (const [floor, floorSensors] of sensorsByFloor.entries()) {
            localContext = this.processAll({ ...localContext, sensors: floorSensors });
            sensorsByFloor.set(floor, localContext.sensors);
            this.previousFloor = localContext.sensors;
        }
        this.previousFloor = [];
        const mergedSensors = Array.from(sensorsByFloor.values()).flat();
        return { ...context, sensors: mergedSensors };
    }

    protected processByColumn(context: StageContext): StageContext {
        const fullVolume = Grid.sensorPoints;
        const maxZ = Grid.gridZ;

        // Create a map: id -> original index in context.sensors
        const sensorIndexMap = new Map(context.sensors.map((sd, index) => [sd.getID(), index]));

        // Create a map: id -> Sensor data
        const sensorMap = new Map(context.sensors.map((sd) => [sd.getID(), sd]));

        const sensors = [];
        let localContext: StageContext = context;

        for (let i = 0; i < fullVolume.length; i += maxZ) {
            // This is an array of ids per column
            const columnIDs = fullVolume
                .slice(i, i + maxZ)
                .filter((sp) => !sp.isNull())
                .map((sp) => sp.getID());

            // Map these ids to a column of sensor data, preserving original order
            const columnSensors = columnIDs
                .map((id) => sensorMap.get(id))
                .filter((sd): sd is SensorData => !!sd)
                .sort((a, b) => {
                    const indexA = sensorIndexMap.get(a.getID()) ?? Infinity;
                    const indexB = sensorIndexMap.get(b.getID()) ?? Infinity;
                    return indexA - indexB;
                });

            if (columnSensors.length === 0) continue;

            // Send the column to the stage
            localContext = this.processAll({ ...localContext, sensors: columnSensors });

            // Add the stage output to the output
            sensors.push(...localContext.sensors);
        }

        return { ...context, sensors };
    }

    /**
     * Process a single sensor data
     * @param input
     */
    protected abstract process(input: SensorData, context: StageContext): SensorData | null;

    /**
     * get Parameters
     */
    protected getParams(): T {
        return this.params;
    }

    protected setParams(params: T) {
        this.params = params;
    }

    protected getPreviousFloor(): SensorData[] {
        return this.previousFloor;
    }

    protected notify(type: NotificationType, message: string, id?: string, show?: boolean) {
        this.notifications.push({ type, message, id, show });
    }

    public getNotifications(): Notification[] {
        const notificaiton = this.notifications;
        this.clearNotifications();
        return notificaiton;
    }

    public clearNotifications() {
        this.notifications = [];
    }
}

/**
 * Abstract base class for filter stages
 */
export abstract class FilterStage<T extends StageParameters> extends PipelineStage<T> {
    constructor(params: T) {
        super(params, StageType.FILTER);
    }

    protected override validate(input: StageContext, output: StageContext): void {
        // Runtime validation
        if (output.sensors.length > input.sensors.length) {
            throw new Error(
                `Filter stage "${this.constructor.name}" increased data size: ` + `${output.sensors.length} > ${input.sensors.length}`
            );
        }
    }

    protected override process(input: SensorData, context: StageContext): SensorData | null {
        return this.filter(input, context) ? input : null;
    }

    protected abstract filter(input: SensorData, context: StageContext): boolean;
}

/**
 * Abstract base class for transform stages
 */
export abstract class TransformStage<T extends StageParameters> extends PipelineStage<T> {
    constructor(params: T) {
        super(params, StageType.TRANSFORM);
    }

    protected override validate(input: StageContext, output: StageContext): void {
        // Runtime validation
        if (output.sensors.length !== input.sensors.length) {
            throw new Error(
                `Transform stage "${this.constructor.name}" changed data size: ` + `${output.sensors.length} != ${input.sensors.length}`
            );
        }
    }

    // Transform stages must implement process directly
    protected abstract override process(input: SensorData, context: StageContext): SensorData;
}

export abstract class PreprocessStage<T extends StageParameters> extends PipelineStage<T> {
    constructor(params: T) {
        super(params, StageType.PREPROCESS);
    }

    protected override validate(input: StageContext, output: StageContext): void {
        // Runtime validation
        if (output.sensors.length != input.sensors.length) {
            throw new Error(
                `Preprocess stage "${this.constructor.name}" changed data size: ${output.sensors.length} != ${input.sensors.length}`
            );
        }
    }

    protected override processAll(input: StageContext): StageContext {
        return this.transformContext(input);
    }

    protected override process(input: SensorData): SensorData {
        return input;
    }

    protected abstract transformContext(input: StageContext): StageContext;
}
